import {
  getDateFromIso,
  getTimeFromIso,
  isFrozenTaskDate,
} from '@nursio-link/common/utils';
import dayjs from 'dayjs';
import {z} from 'zod';

import i18n from '@/i18n';
import type {
  CreateRecurringTaskInput,
  CreateTaskInput,
  PartOfTheDay,
  RecurringTask,
  Task,
} from '@/services/graphqlHooks';
import {DurationType} from '@/services/graphqlHooks';
import {TIME_FORMATS} from '@/utils/timeFormats';

import {
  getTaskLocalDate,
  getTaskLocalDateTime,
  isStandardTask,
} from './tasksHelpers';

export enum TaskType {
  Standard = 'standard',
  Announcement = 'announcement',
}

export enum StandardTaskVariant {
  Recurring = 'recurring',
  OneTime = 'one-time',
}

export enum OccurrencesType {
  Never = 'never',
  After = 'after',
}

export const taskActionFormSchema = z
  .object({
    mode: z.enum(['none', 'reject', 'comment', 'update-comment'] as const),
    rejectionNote: z.string().optional(),
    comment: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mode === 'reject' && !data.rejectionNote?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['rejectionNote'],
        message: i18n.t('Reason is required'),
      });
    }
    if (data.mode === 'comment' && !data.comment?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['comment'],
        message: i18n.t('Comment is required'),
      });
    }
  });

export type TaskActionFormValues = z.infer<typeof taskActionFormSchema>;

export const taskActionFormDefaultValues: TaskActionFormValues = {
  mode: 'none',
  rejectionNote: '',
  comment: '',
};

export const taskFormSchema = z
  .object({
    title: z.string().trim().min(1, i18n.t('Title is required')),
    content: z.string().trim().optional(),
    assignedToEmployee: z.string().optional(),
    assignedToResident: z.string().optional(),
    jobRoleIds: z.array(z.string()),
    time: z.string().optional(),
    date: z.string().optional(),

    // Recurring task fields
    startTimestamp: z.string().optional(),
    occurrences: z.number().optional(),
    durationType: z.string().optional(),
    durationAmount: z.number().min(1).optional(),

    // Helper fields
    taskType: z.enum(TaskType),
    standardTaskVariant: z.enum(StandardTaskVariant),
    occurrencesType: z.enum(OccurrencesType),
  })
  .superRefine((data, ctx) => {
    if (data.taskType === TaskType.Standard && !data.time) {
      ctx.addIssue({
        code: 'custom',
        path: ['time'],
        message: i18n.t('Time is required'),
      });
    }

    if (data.standardTaskVariant === StandardTaskVariant.OneTime) {
      if (isFrozenTaskDate(data.date)) {
        ctx.addIssue({
          code: 'custom',
          path: ['date'],
          message: i18n.t(
            'This month is archived and can no longer be changed'
          ),
        });
      }
      return;
    }

    if (!data.startTimestamp) {
      ctx.addIssue({
        code: 'custom',
        path: ['startTimestamp'],
        message: i18n.t('Start from is required'),
      });
    }

    if (!data.durationType) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationType'],
        message: i18n.t('Duration type is required'),
      });
    }

    if (!data.durationAmount) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationAmount'],
        message: i18n.t('Duration amount is required'),
      });
    }
  });

export type TaskFormValues = z.infer<typeof taskFormSchema>;
export type TaskFromDefaultValueInput = Task & RecurringTask;

export const taskFormDefaultValues = (
  task?: TaskFromDefaultValueInput
): TaskFormValues => {
  const date = task?.date
    ? getTaskLocalDate(task)
    : dayjs().format(TIME_FORMATS.awsDate);

  const localDateTime = task ? getTaskLocalDateTime(task) : undefined;

  const timeDate =
    task?.date ?? (task?.startTimestamp && getDateFromIso(task.startTimestamp));

  const time = localDateTime
    ? localDateTime.format(TIME_FORMATS.time)
    : task?.completeUntil?.time && timeDate
    ? dayjs(`${timeDate}T${task.completeUntil.time}`).format(TIME_FORMATS.time)
    : task?.completeUntil?.partOfTheDay || '';

  const occurrencesNeverEnds = task?.occurrences && task?.occurrences < 0;

  const taskType = task
    ? isStandardTask(task)
      ? TaskType.Standard
      : TaskType.Announcement
    : TaskType.Standard;

  return {
    title: task?.title || '',
    content: task?.content || '',
    assignedToEmployee: task?.assignedToEmployee || '',
    assignedToResident: task?.assignedToResident || '',
    jobRoleIds: task?.jobRoleIds ?? [],
    date,
    time,

    // Recurring task fields
    durationAmount: task?.interval?.amount || 1,
    durationType: task?.interval?.type || DurationType.Day,
    startTimestamp: dayjs(task?.startTimestamp).format(TIME_FORMATS.awsDate),
    occurrences: occurrencesNeverEnds ? 1 : task?.occurrences,

    // Helper fields
    taskType,
    standardTaskVariant: StandardTaskVariant.OneTime,
    occurrencesType: occurrencesNeverEnds
      ? OccurrencesType.Never
      : OccurrencesType.After,
  };
};

const getCompleteUntil = (
  values: TaskFormValues,
  isTimeFormat: boolean,
  date?: string
) => {
  if (values.taskType === TaskType.Announcement) return undefined;

  return values.time
    ? {
        partOfTheDay: !isTimeFormat ? (values.time as PartOfTheDay) : undefined,
        time: isTimeFormat ? getTime(values.time, date) : undefined,
      }
    : undefined;
};

export const mapTaskFormValuesToTaskInput = (
  values: TaskFormValues
): CreateTaskInput => {
  const isTimeFormat = !!(values.time && values.time.includes(':'));

  const calculatedDate = isTimeFormat
    ? getDateFromIso(dayjs(`${values.date}T${values.time}`).toISOString())
    : values.date;

  const completeUntil = getCompleteUntil(values, isTimeFormat, values.date);

  return {
    title: values.title,
    content: values.content,
    assignedToEmployee: values.assignedToEmployee,
    assignedToResident: values.assignedToResident,
    jobRoleIds: values.jobRoleIds,
    date: calculatedDate,
    completeUntil,
  };
};

export const mapTaskFormValuesToRecurringTaskInput = (
  values: TaskFormValues
): CreateRecurringTaskInput => {
  const isTimeFormat = !!(values.time && values.time.includes(':'));
  const calculatedDateTime = isTimeFormat
    ? dayjs(`${values.startTimestamp}T${values.time}`).toISOString()
    : `${values.startTimestamp}T${getTimeFromIso(new Date().toISOString())}`;
  const occurrences =
    values.occurrencesType === OccurrencesType.Never ? -1 : values.occurrences;

  const completeUntil = getCompleteUntil(
    values,
    isTimeFormat,
    values.startTimestamp
  );

  return {
    title: values.title,
    content: values.content,
    assignedToEmployee: values.assignedToEmployee,
    assignedToResident: values.assignedToResident,
    jobRoleIds: values.jobRoleIds,
    completeUntil,
    startTimestamp: calculatedDateTime,
    timeZone: dayjs.tz.guess(),
    occurrences: occurrences || 1,
    interval: {
      type: (values.durationType as DurationType) || DurationType.Day,
      amount: values.durationAmount || 1,
    },
  };
};

export const getTime = (
  time: string,
  date = dayjs().format(TIME_FORMATS.awsDate)
): string => getTimeFromIso(dayjs(`${date}T${time}`).toISOString());
